<template>
  <div :class="controlClasses" class="mds-form-control mds-switch">
    <label :style="controlLabelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row"
           @mouseenter="$core.tooltip.show($event.currentTarget, disabledReasons)"
           @mouseleave="$core.tooltip.hide()"
      >
        <input
          :id="uid"
          @change="onChange"
          ref="checkbox"
          type="checkbox"
          :disabled="disabled"
          :checked="isChecked"
        />

        <label v-if="isSliderTheme"
               :for="uid"
               tabindex="0"
               @keydown.space.prevent="onKeyToggle"
               @keydown.enter.prevent="onKeyToggle">
          <div class="label-text checked">{{ checkedLabel }}</div>
          <div class="mds-switch-control-dot" />
          <div class="label-text unchecked">{{ uncheckedLabel }}</div>
        </label>

        <label v-else :for="uid"
               tabindex="0"
               @keydown.space.prevent="onKeyToggle"
               @keydown.enter.prevent="onKeyToggle">
          <div class="mds-switch-control">
            <div class="mds-switch-control-dot" />
          </div>
          <slot />
        </label>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VSwitch.scss" />

<script lang="ts">
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";

const SWITCHER_THEME_LIST = ["default", "slider"];

export default defineComponent({
  name: "VSwitch",

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: {
      type:    [Boolean, Number, String, Object, Array],
      default: null,
    },
    /** Отмеченное значение */
    checkedValue: {
      type:    [Boolean, Number, String, Object, Array],
      default: true,
    },
    /** Неотмеченное значение */
    uncheckedValue: {
      type:    [Boolean, Number, String, Object, Array],
      default: false,
    },
    /** Лейбл "отмечено" (только для slider-темы) */
    checkedLabel: {
      type:    [Boolean, Number, String, Object, Array],
      default: "Да",
    },
    /** Лейбл "не отмечено" (только для slider-темы) */
    uncheckedLabel: {
      type:    [Boolean, Number, String, Object, Array],
      default: "Нет",
    },
    /** Тема свитчера */
    theme: {
      type:      String,
      default:   "default",
      validator: (v: string) => SWITCHER_THEME_LIST.includes(v),
    },
  },

  emits: ["update:modelValue"],

  computed: {
    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      classes.push(this.theme);
      return classes;
    },

    controlLabelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    /**
     * Проверка отмеченности.
     *
     * Сравниваем modelValue сначала с checkedValue, затем с uncheckedValue —
     * независимо от их типов. Это позволяет менять checkedValue/uncheckedValue
     * местами (например :checked-value="false" :unchecked-value="true").
     *
     * Если modelValue не совпало ни с одним из значений (третье состояние,
     * например null) — свитч считается неотмеченным.
     */
    isChecked(): boolean {
      if (Core.utils.isEqual(this.modelValue, this.checkedValue) === true) {
        return true;
      } else if (Core.utils.isEqual(this.modelValue, this.uncheckedValue) === true) {
        return false;
      } else {
        return false;
      }
    },

    isDefaultTheme(): boolean {
      return this.theme === "default";
    },

    isSliderTheme(): boolean {
      return this.theme === "slider";
    },
  },

  watch: {
    modelValue(newVal, oldVal) {
      this.validation.markTouched();
      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validation.markDirty();
        this.validate();
      }
    },
  },

  methods: {
    onKeyToggle() {
      if (this.disabled === true) return;
      const newValue = this.isChecked === true ? this.uncheckedValue : this.checkedValue;
      this.$emit('update:modelValue', newValue);
    },

    /**
     * Новое значение считаем от isChecked (источник истины — modelValue), а не от
     * event.target.checked. Нативный checkbox уже переключил себя сам, и если
     * родитель не примет новое значение (или примет то же самое), биндинг :checked
     * не изменится и Vue не перерисует DOM — свитч останется рассинхронизированным
     * с моделью. Поэтому сразу возвращаем DOM в состояние, соответствующее пропу.
     */
    onChange(event) {
      let value = null;

      if (this.isChecked === true) {
        value = this.uncheckedValue;
      } else {
        value = this.checkedValue;
      }

      event.target.checked = this.isChecked;

      this.$emit("update:modelValue", value);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;

      await this.validation.validate(this.modelValue);

      if (this.required === true) {
        this.validateRequired();
      }
    },

    /**
     * Дописывает к стандартной валидации правило required для свитча.
     *
     * У свитча значение false — валидное, поэтому стандартный required (который
     * считает любое пустое значение ошибкой) не подходит. На свитче required
     * означает «свитч должен стоять в checked-положении».
     *
     * Метод проверяет что modelValue равно uncheckedValue, и если да — пушит
     * ошибку и сбрасывает isValid.
     */
    validateRequired(): void {
      // Если ошибки уже есть от других правил — не дублируем required.
      if (this.validation.errors.length > 0) return;

      const isUnchecked = this.isInUncheckedState();

      if (isUnchecked === true) {
        this.validation.errors.push("Поле обязательно к заполнению");
        this.validation.isValid = false;
      }
    },

    /**
     * Свитч сейчас в положении "не отмечено"?
     *
     * Случай 1: uncheckedValue === null (свитч с тремя состояниями) — считаем
     *           свитч неотмеченным, если modelValue falsy (null, undefined, false, 0).
     * Случай 2: uncheckedValue задано явно (например false или "no") — сравниваем
     *           modelValue с ним через isEqual (на случай объектов/массивов).
     */
    isInUncheckedState(): boolean {
      if (this.uncheckedValue === null) {
        return Boolean(this.modelValue) === false;
      }
      return Core.utils.isEqual(this.modelValue, this.uncheckedValue);
    },
  },
});
</script>